import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsByAgentIdOptions, getOrgCredentialsOptions, getOrgMembersOptions } from '@onefootprint/fetch-dashboard';
import { IcoPlusSmall16 } from '@onefootprint/icons';
import { getErrorMessage } from '@onefootprint/request';
import { Button, Divider } from '@onefootprint/ui';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionHeader from '@/components/section-header';
import useSession from '@/hooks/use-session';
import AddCredentialDialog from './components/add-credential-dialog';
import CredentialsList from './components/credentials-list';
import type { CredentialsScope } from './types';
type AgentCredentialsProps = {
  title: string;
  subtitle: string;
  agentId?: string;
};
const AgentCredentials = t0 => {
  const $ = _c(47);
  const {
    title,
    subtitle,
    agentId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "credentials"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    data: t2
  } = useSession();
  const {
    user
  } = t2;
  const [adding, setAdding] = useState(false);
  let t3;
  if ($[1] !== agentId) {
    t3 = getOrgCredentialsOptions(agentId ? {
      query: {
        agentId
      }
    } : undefined);
    $[1] = agentId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: credentials,
    isPending,
    error
  } = useQuery(t3);
  let saved;
  let t4;
  let t5;
  if ($[3] !== agentId || $[4] !== credentials) {
    saved = credentials?.filter(_temp);
    const agentIds = agentId ? [] : [...new Set(saved?.flatMap(_temp2))];
    t4 = useQueries;
    t5 = {
      queries: agentIds.map(_temp3)
    };
    $[3] = agentId;
    $[4] = credentials;
    $[5] = saved;
    $[6] = t4;
    $[7] = t5;
  } else {
    saved = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  const agentQueries = t4(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getOrgMembersOptions({
      query: {
        pageSize: 100
      }
    });
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = !!agentId;
  let t8;
  if ($[9] !== t7) {
    t8 = {
      ...t6,
      enabled: t7
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const {
    data: members,
    isPending: isMembersPending
  } = useQuery(t8);
  let t9;
  if ($[11] !== agentId || $[12] !== agentQueries || $[13] !== isMembersPending || $[14] !== isPending) {
    t9 = isPending || (agentId ? isMembersPending : agentQueries.some(_temp4));
    $[11] = agentId;
    $[12] = agentQueries;
    $[13] = isMembersPending;
    $[14] = isPending;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const isLoading = t9;
  let t10;
  if ($[16] !== agentId || $[17] !== agentQueries || $[18] !== members?.data || $[19] !== user?.id) {
    t10 = agentId ? {
      kind: "agent",
      agentId,
      currentUserId: user?.id ?? "",
      members: members?.data ?? []
    } : {
      kind: "user",
      agents: agentQueries.flatMap(_temp5)
    };
    $[16] = agentId;
    $[17] = agentQueries;
    $[18] = members?.data;
    $[19] = user?.id;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const scope = t10;
  let t11;
  if ($[21] !== agentId || $[22] !== subtitle || $[23] !== title) {
    t11 = agentId ? <div className="mb-8 flex flex-col gap-1"><h2 className="text-heading-2 text-primary">{title}</h2><p className="text-body-2 text-tertiary">{subtitle}</p></div> : <><SectionHeader title={title} subtitle={subtitle} /><Divider className="mb-6" /></>;
    $[21] = agentId;
    $[22] = subtitle;
    $[23] = title;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== error) {
    t12 = error ? getErrorMessage(error) : undefined;
    $[25] = error;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== isLoading || $[28] !== saved || $[29] !== scope || $[30] !== t12) {
    t13 = <CredentialsList credentials={saved} scope={scope} isLoading={isLoading} errorMessage={t12} />;
    $[27] = isLoading;
    $[28] = saved;
    $[29] = scope;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setAdding(true);
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t) {
    t15 = t(_temp6);
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <Button variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={t14}>{t15}</Button>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t13 || $[38] !== t16) {
    t17 = <div className="flex flex-col items-center gap-2">{t13}{t16}</div>;
    $[37] = t13;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== adding || $[41] !== scope) {
    t18 = adding && <AddCredentialDialog scope={scope} onClose={() => setAdding(false)} />;
    $[40] = adding;
    $[41] = scope;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t11 || $[44] !== t17 || $[45] !== t18) {
    t19 = <section>{t11}{t17}{t18}</section>;
    $[43] = t11;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
};
export default AgentCredentials;
function _temp(credential) {
  return !credential.expiresAt;
}
function _temp2(credential_0) {
  return credential_0.allowedAgentIds;
}
function _temp3(id) {
  return getOrgAgentsByAgentIdOptions({
    path: {
      agentId: id
    }
  });
}
function _temp4(query) {
  return query.isPending;
}
function _temp5(query_0) {
  return query_0.data ? [query_0.data] : [];
}
function _temp6($) {
  return $.add;
}